import { useContext, useEffect, useRef, useState } from "react";

import { ProjectPermissionConstants } from "../../../constants";
import { ProjectContext } from "../../../route/shared/projects/project/ProjectContext";
import { logError } from "../../../service/error";
import { getCurrentUserProjectPermissions } from "../../../service/query";
import { Status } from "../../../service/Shared";
import { Toast } from "../../../widget";

interface UseIssuanceTransactionPermissionReturnData {
  canManageIssuances: boolean;
  isPermissionLoading: boolean;
}

// A verdict is only ever true for the project it was read against — see canManageIssuances below.
interface PermissionVerdict {
  projectUuid: string;
  canManage: boolean;
}

/*
  Whether the acting organisation may manage the issuances of the project a transaction belongs to.

  The transaction panels are opened from two hosts: a project's inventory, which sits inside ProjectProvider and
  already holds the permission list, and the org-wide inventory dashboard, which does not. Reading the context
  alone meant the dashboard answered "no" to everything — not a permission decision but an absent provider —
  which silently withheld the whole attachments section there. Same defect WorkflowTaskPanel fixed for uploads
  via useDocumentUploadPermission (KAN-9638); this asks the same question for manageProjectIssuances.

  Denies while the answer is in flight, so the panel never offers an edit it is about to withdraw.
*/
export const useIssuanceTransactionPermission = (
  projectUuid: string | null,
  // Whether the host panel is open — the read follows the panel rather than the row's mount.
  show: boolean
): UseIssuanceTransactionPermissionReturnData => {
  /*
    Optional call: outside ProjectProvider the context is the empty default, so hasProjectPermission is
    undefined and the read below answers instead.
  */
  const { hasProjectPermission } = useContext(ProjectContext);

  const [verdict, setVerdict] = useState<PermissionVerdict | null>(null);
  const [isPermissionLoading, setIsPermissionLoading] = useState(false);
  // Only the newest read may write the verdict — the panel is reopened on a transaction in another project.
  const requestIdRef = useRef(0);

  /*
    In-context callers are answered directly: the project route already paid for the permission list, so asking
    the store a second time would be a wasted round trip on every panel open.
  */
  const contextAnswer = hasProjectPermission?.(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES);

  /*
    Derived during render rather than reset in the effect, so a verdict read for the previous project can never
    be applied to the current one — not even for the single commit before the new read starts.
  */
  const canManageIssuances =
    contextAnswer ?? (projectUuid !== null && verdict?.projectUuid === projectUuid && verdict.canManage);

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    /*
      Retires this read when the panel closes or the project changes. Without it a failure landing after the
      user has moved on still counts as current, and the toast would arrive over whatever they opened next.
    */
    const retireRead = (): void => {
      requestIdRef.current += 1;
    };

    // Nothing to ask: either the context already answered, or there is no project to ask about.
    if (contextAnswer !== undefined || !show || !projectUuid) {
      setIsPermissionLoading(false);
      return retireRead;
    }

    /*
      A failed read is not a grant, so editing stays withheld rather than being offered for the API to refuse.
      Reported rather than swallowed: denying silently would make an outage look exactly like a permission the
      user does not hold.
    */
    const reportFailure = (error: Error | string): void => {
      logError({ error });
      if (requestId !== requestIdRef.current) return;
      setVerdict({ projectUuid, canManage: false });
      Toast.error({ message: "Transaction permissions could not be checked. Please try again." });
    };

    setIsPermissionLoading(true);

    getCurrentUserProjectPermissions({ projectUuids: [projectUuid], groupUuids: null })
      .then((response) => {
        /*
          The service wrappers resolve an error response instead of rejecting, so a failed read arrives here
          rather than in the catch below.
        */
        if (response.status !== Status.Success) {
          reportFailure(`Could not read the project issuance permission: ${JSON.stringify(response.errors ?? [])}`);
          return;
        }

        if (requestId !== requestIdRef.current) return;

        setVerdict({
          projectUuid,
          canManage: (response.data?.[0]?.currentUserPermissions ?? []).includes(
            ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES
          ),
        });
      })
      .catch((error) => reportFailure(error))
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsPermissionLoading(false);
      });

    return retireRead;
  }, [projectUuid, show, contextAnswer]);

  return { canManageIssuances, isPermissionLoading };
};
